import * as React from "react";
import { SectionShell } from "@/components/design-system/SectionShell";
import { InlineField, useInlineEdit } from "@/components/ui/inline-edit";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

const code = `import { InlineField, useInlineEdit } from "@/components/ui/inline-edit"

// One field on its own
const heading = useInlineEdit(section.heading, (next) =>
  inlinePatch(url, { page_section: { heading: next } }),
)

<InlineField label="Heading" htmlFor="heading" state={heading}>
  <Input
    id="heading"
    value={heading.value}
    onChange={(e) => heading.set(e.target.value)}
    onKeyDown={heading.keys}
  />
</InlineField>

// A whole record — one hook, any number of named fields, no hook per box
import { useInlineRecord } from "@/hooks/use-inline-record"

const fields = useInlineRecord({
  record: { label: condition.label, status: condition.status },
  url: \`/admin/website/conditions/\${condition.id}\`,
  param: "condition",
  enabled: !isNew,          // a "new" screen keeps its Create button
})
const label = fields.state("label")

<InlineField label="Name" htmlFor="label" error={errors.label} state={label}>
  <Input
    id="label"
    value={fields.value("label")}
    onChange={(e) => fields.set("label", e.target.value)}
    onKeyDown={label.keys}
  />
</InlineField>`;

function PreviewGroup() {
  const [savedHeading, setSavedHeading] = React.useState("A calmer place to think");
  const [savedStatus, setSavedStatus] = React.useState("draft");

  // Stands in for the round trip so the preview behaves like the real thing.
  const settle = <T,>(commit: (value: T) => void) => (next: T) =>
    new Promise<void>((resolve) =>
      setTimeout(() => {
        commit(next);
        resolve();
      }, 400),
    );

  const heading = useInlineEdit(savedHeading, settle(setSavedHeading));
  const status = useInlineEdit(savedStatus, settle(setSavedStatus));

  return (
    <div className="max-w-md space-y-5">
      <InlineField
        label="Intro — bold lead line"
        htmlFor="ds-inline-heading"
        hint="Type something to see the tick and the cross appear."
        state={heading}
      >
        <Input
          id="ds-inline-heading"
          value={heading.value}
          onChange={(e) => heading.set(e.target.value)}
          onKeyDown={heading.keys}
        />
      </InlineField>

      <InlineField label="Visibility" htmlFor="ds-inline-status" state={status}>
        <Select
          id="ds-inline-status"
          value={status.value}
          onChange={(e) => status.set(e.target.value)}
        >
          <option value="draft">Draft — hidden from visitors</option>
          <option value="published">Published — live on the site</option>
        </Select>
      </InlineField>

      <p className="text-sm text-ink-muted">
        Saved: <strong>{savedHeading || "—"}</strong> · {savedStatus}
      </p>
    </div>
  );
}

export function InlineEditSection() {
  return (
    <SectionShell
      id="inline-edit"
      title="Inline edit"
      description={
        <>
          A field that keeps its own change until it is confirmed. The moment
          its value differs from what the server holds, a{" "}
          <strong>tick and a cross</strong> appear beneath it: the tick commits
          that one field, the cross puts it back. Nothing else on the screen is
          touched, so there is no whole-form Save button to hunt for and no way
          to lose an edit by leaving one behind. <kbd>Enter</kbd> saves a
          single-line field, <kbd>Esc</kbd> reverts, and{" "}
          <kbd>Cmd/Ctrl+Enter</kbd> saves a textarea.
        </>
      }
      whenToUse={
        <ul>
          <li>Every editable field on a screen that edits an existing record.</li>
          <li>
            Copy, settings and metadata on the Website page screens, where a
            page carries dozens of independent boxes.
          </li>
          <li>
            Anywhere a shared Save button would let one field's half-finished
            edit ride along with another's deliberate change.
          </li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>
            A "new" screen — there is no record to PATCH into, so it keeps one
            Create button. Pass <code>enabled: false</code> and the same boxes
            behave as ordinary controlled inputs.
          </li>
          <li>
            A yes/no that should take effect at once — use{" "}
            <code>&lt;Switch&gt;</code>.
          </li>
          <li>
            A picked or uploaded photo: you can see whether it is the right
            one, so it saves on choosing.
          </li>
          <li>
            Structural changes — adding, removing or reordering a list — which
            commit on the click, the way the ranked photo slots do.
          </li>
        </ul>
      }
      preview={<PreviewGroup />}
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>
            <code>useInlineEdit(committed, onSave)</code> — one field.{" "}
            <code>onSave</code> is given the draft and resolves when the server
            has taken it; rejecting shows the message in place.
          </li>
          <li>
            <code>useInlineRecord({"{ record, url, param }"})</code> — a whole
            record. Keys are addressed by name rather than by a hook each,
            because admin forms show and hide fields as you fill them in.
          </li>
          <li>
            <code>state.value</code> / <code>state.set</code> — bind the
            control to the draft, never to the server value.
          </li>
          <li>
            <code>state.keys</code> / <code>state.multilineKeys</code> —{" "}
            Enter/Esc handlers for a single-line control and a textarea.
          </li>
          <li>
            <code>state.version</code> — bump-on-reset, for remounting an
            uncontrolled control (<code>&lt;RichTextField&gt;</code>) when a
            change is discarded or an AI draft lands.
          </li>
          <li>
            <code>serialize</code> / <code>with</code> — what goes on the wire,
            and any key that must travel with this one (latitude and longitude
            are a pair the model refuses half of).
          </li>
          <li>
            <code>&lt;InlineEditBar&gt;</code> — the tick and cross on their
            own, for a group of controls that is one answer (a set of
            checkboxes, a list of phrases).
          </li>
          <li>
            The wrapper carries <code>data-inline-field="{"{htmlFor}"}"</code>,
            which is how a system test presses one field's tick.
          </li>
        </ul>
      }
    />
  );
}
